// Botón + menú "Ayuda de IA" del composer de "Crear desde cero".
// Ocupa el lugar exacto del viejo selector de Inteligencia.
//
// El submenú de Inteligencia se comporta distinto por breakpoint:
//   • DESKTOP (md+) → flyout: la lista se abre AL COSTADO de la opción, con
//     el menú raíz siempre a la vista. La fila queda marcada mientras está
//     abierta.
//   • MOBILE       → drill-down de dos páginas con "Volver", el patrón del
//     menú "+" del composer.
// La bifurcación usa `useIsMdUp` (el MISMO breakpoint que las clases `md:`)
// para renderizar una sola vez y no duplicar los botones en el DOM.
//
// Togglear "Ayuda de IA" NO cierra el menú: el cambio de modo es sutil (el
// aura hace fade, el título transiciona de color) y cerrar el menú de golpe
// rompía esa sensación.
//
// Ver ADR-2026-08-19-modo-directo-crear-desde-cero.

import React, { useState, useEffect, useRef } from 'react';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Check,
  Brain,
} from 'lucide-react';
import { type Intelligence, INTELLIGENCE_LABEL } from '../../agentModels';
import { useIsMdUp } from './useIsMdUp';

/** Los tres niveles con su copy. Exportado porque el selector del Chat ofrece los
 *  mismos (ADR-2026-09-08-el-chat-toma-el-motor-del-asistente). */
export const INTEL_OPTIONS: { id: Intelligence; label: string; desc: string }[] = [
  {
    id: 'baja',
    label: 'Baja',
    desc: 'Rápida · el razonamiento no consume créditos',
  },
  {
    id: 'media',
    label: 'Media',
    desc: 'Más inteligente · el razonamiento consume créditos',
  },
  {
    id: 'alta',
    label: 'Alta',
    desc: 'Máxima inteligencia · razonamiento más caro',
  },
];

/** Ancho del flyout de Inteligencia y su separación del menú, en px. Se usan
 *  para medir si entra a la derecha (ver `flyoutSide`). */
const FLYOUT_WIDTH = 264;
const FLYOUT_GAP = 8;

export interface AyudaIAMenuProps {
  aiHelp: boolean;
  onToggleAiHelp: (on: boolean) => void;
  intelligence: Intelligence;
  onChangeIntelligence: (i: Intelligence) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AyudaIAMenu: React.FC<AyudaIAMenuProps> = ({
  aiHelp,
  onToggleAiHelp,
  intelligence,
  onChangeIntelligence,
  open,
  onOpenChange,
}) => {
  const isMdUp = useIsMdUp();
  const [subOpen, setSubOpen] = useState(false);
  // El flyout va a la DERECHA del menú. Pero este menú está anclado al borde
  // derecho del composer, así que en pantallas angostas no siempre entra — y
  // el sheet tiene `md:overflow-hidden`, o sea que lo que no entra no se
  // recorta feo: DESAPARECE. Medimos al abrir y caemos a la izquierda solo
  // si hace falta.
  const panelRef = useRef<HTMLDivElement>(null);
  const [flyoutSide, setFlyoutSide] = useState<'right' | 'left'>('right');
  useEffect(() => {
    if (!subOpen || !isMdUp) return;
    const el = panelRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // El sheet llega hasta el borde derecho del viewport, así que ese es el
    // límite real. +16 de aire para no pegarlo al filo.
    const espacioDerecha = window.innerWidth - rect.right;
    setFlyoutSide(
      espacioDerecha >= FLYOUT_WIDTH + FLYOUT_GAP + 16 ? 'right' : 'left',
    );
  }, [subOpen, isMdUp]);

  // Al cerrar el menú, el submenú se resetea: reabrirlo parado en la
  // sub-vista sería desorientador.
  useEffect(() => {
    if (!open) setSubOpen(false);
  }, [open]);

  // Apagar la Ayuda de IA con el submenú abierto lo cierra: sin agente, la
  // inteligencia no aplica.
  useEffect(() => {
    if (!aiHelp) setSubOpen(false);
  }, [aiHelp]);

  const intelContent = (showBack: boolean) => (
    <div className="flex flex-col gap-1">
      {showBack && (
        <button
          type="button"
          onClick={() => setSubOpen(false)}
          className="self-start inline-flex items-center gap-0.5 px-2 py-2 text-[13.5px] text-brand-primary hover:opacity-75 transition-opacity"
        >
          <ChevronLeft className="w-4 h-4" />
          Volver
        </button>
      )}
      {INTEL_OPTIONS.map((opt) => {
        const active = intelligence === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => {
              onChangeIntelligence(opt.id);
              // En mobile volvemos al root; en desktop el flyout queda
              // abierto para que se vea el tilde moverse.
              if (!isMdUp) setSubOpen(false);
            }}
            className={`w-full text-left rounded-xl px-3 py-2.5 transition-colors ${
              active ? 'bg-[rgb(var(--brand-primary-rgb)_/_0.06)]' : 'hover:bg-neutral-50'
            }`}
          >
            <span className="flex items-center justify-between gap-2">
              <span className="text-[14px] font-medium text-[rgb(var(--brand-black-rgb)_/_0.9)]">
                {opt.label}
              </span>
              {active && (
                <Check className="w-4 h-4 text-brand-primary shrink-0" />
              )}
            </span>
            <span className="block text-[12px] text-[#3C3C43]/55 mt-0.5 leading-snug">
              {opt.desc}
            </span>
          </button>
        );
      })}
      {/* ADR-608: lo que cambia por nivel es el costo del RAZONAMIENTO; las
          generaciones cobran aparte igual. */}
      <p className="px-3 pt-2 pb-1.5 text-[11px] text-[#3C3C43]/45 leading-snug border-t border-black/[0.05] mt-1">
        Las imágenes, videos y audios que genere el asistente consumen créditos
        aparte, en cualquier nivel.
      </p>
    </div>
  );

  const rootContent = (
    <div className="flex flex-col gap-1">
      {/* Fila del toggle. No usa AttachMenuItem porque termina en un switch,
          no en un chevron. */}
      <div className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl">
        <span className="min-w-0">
          <span className="block text-[14.5px] font-medium text-[rgb(var(--brand-black-rgb)_/_0.9)]">
            Ayuda de IA
          </span>
          <span className="block text-[12px] text-[#3C3C43]/55 mt-0.5 leading-snug">
            Atlas conversa, elige y arma el pedido por vos.
          </span>
        </span>
        {/* Switch accesible — mismo token que VoiceGuionSection. */}
        <button
          type="button"
          role="switch"
          aria-checked={aiHelp}
          aria-label="Ayuda de IA"
          onClick={() => onToggleAiHelp(!aiHelp)}
          className={`relative w-[51px] h-[31px] rounded-full transition-colors duration-300 shrink-0 ${
            aiHelp ? 'bg-brand-primary' : 'bg-black/[0.09]'
          }`}
        >
          <div
            className={`absolute top-[2px] w-[27px] h-[27px] bg-white rounded-full shadow-[0_2px_4px_rgba(0,0,0,0.15)] transition-transform duration-300 ${
              aiHelp ? 'translate-x-[22px]' : 'translate-x-[2px]'
            }`}
          />
        </button>
      </div>

      <div className="border-t border-black/[0.05] my-1" />

      {/* Inteligencia: solo tiene sentido si hay agente. */}
      <button
        type="button"
        disabled={!aiHelp}
        onClick={() => setSubOpen((v) => !v)}
        className={`w-full flex items-center justify-between gap-3 px-3 py-3 rounded-xl text-left transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent ${
          subOpen && isMdUp
            ? 'bg-neutral-100'
            : 'hover:bg-neutral-50 active:bg-neutral-100'
        }`}
      >
        <span className="inline-flex items-center gap-3 text-[14.5px] text-[rgb(var(--brand-black-rgb)_/_0.9)]">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[rgb(var(--brand-primary-rgb)_/_0.1)] text-brand-primary shrink-0">
            <Brain className="w-4 h-4" />
          </span>
          Inteligencia
        </span>
        <span className="inline-flex items-center gap-1 shrink-0">
          <span className="text-[13.5px] text-[#3C3C43]/55">
            {INTELLIGENCE_LABEL[intelligence]}
          </span>
          <ChevronRight className="w-4 h-4 text-[#3C3C43]/40" />
        </span>
      </button>
    </div>
  );

  return (
    <div className="relative shrink-0">
      <button
        type="button"
        onClick={() => onOpenChange(!open)}
        aria-expanded={open}
        // Fondo activo con hex+alpha, NO `bg-brand-primary/[0.08]`:
        // brand-primary es una var() con hex y el modificador de opacidad
        // de Tailwind genera CSS inválido (ADR-140).
        className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full transition-colors duration-300 ${
          aiHelp
            ? 'bg-[rgb(var(--brand-primary-rgb)_/_0.08)] hover:bg-[rgb(var(--brand-primary-rgb)_/_0.12)]'
            : 'bg-neutral-100 hover:bg-neutral-200 md:bg-transparent md:hover:bg-neutral-100'
        }`}
      >
        <span
          className={`text-[14px] whitespace-nowrap transition-colors duration-300 ${
            aiHelp ? 'text-brand-primary font-medium' : 'text-brand-black'
          }`}
        >
          Ayuda de IA
        </span>
        {/* El estado en texto solo en desktop: en mobile "Ayuda de IA
            Desactivada" se come toda la fila. Ahí el estado se lee por el
            color del botón (acento cuando está activa). */}
        <span className="hidden md:inline text-[14px] font-medium text-[#3C3C43]/55 whitespace-nowrap">
          {aiHelp ? 'Activada' : 'Desactivada'}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-[#3C3C43]/45 transition-transform ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-[55]"
            onClick={() => onOpenChange(false)}
          />
          <div
            ref={panelRef}
            className="absolute bottom-full left-0 md:left-auto md:right-0 mb-2 w-[280px] bg-white border border-black/[0.06] rounded-2xl shadow-xl p-1.5 z-[56]"
          >
            {isMdUp || !subOpen ? rootContent : intelContent(true)}
            {/* DESKTOP: flyout al costado, a la DERECHA del menú (con caída a
                la izquierda si no entra, ver `flyoutSide`). */}
            {isMdUp && subOpen && (
              <div
                className={`absolute bottom-0 w-[264px] bg-white border border-black/[0.06] rounded-2xl shadow-xl p-1.5 animate-mode-swap ${
                  flyoutSide === 'right'
                    ? 'left-full ml-2'
                    : 'right-full mr-2'
                }`}
              >
                {intelContent(false)}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AyudaIAMenu;
